import React from "react";
import {Table} from "react-bootstrap";
import useAnalysisData from "./useAnalysisData.js";
import AnalysisCard from "./AnalysisCard.jsx";
import YearValueCells from "./YearValueCells.jsx";
import YearHeaderCells from "./YearHeaderCells.jsx";
import SignedPercentValue from "./SignedPercentValue.jsx";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import {referenceLabels, yearRangeExcludingCurrent} from "./format.js";
import {StickyTableArea} from "../StickyFilterBlock.jsx";

/**
 * Vertreter × Monat: je Vertreter (Ansprechpartner) die Artikelarten mit
 * Jahresspalten, Hochrechnung und den beiden Abweichungsspalten aus der
 * Excel-Sicht "GesamtPersonMonat" (fortschritt.md).
 */
export default function VertreterMonatTable({filters, params, explicitMonths = false}) {
    const {meta} = filters;
    const {data, loading, error} = useAnalysisData("/vertrieb/daten/vertreter-monat", params);

    const pastYears = yearRangeExcludingCurrent(filters.yearFrom, filters.yearTo, meta?.current_year);
    const labels = referenceLabels(data?.reference, meta);
    const hideCurrent = labels.current === null;
    const hideHr = explicitMonths || labels.hr === null;
    // Trend bleibt bei abgeschlossenem Bezugsjahr sinnvoll (R gegen den
    // Durchschnitt der vier Jahre davor, schnittstelle.md 7.2) - nur bei
    // expliziter Monatsauswahl entfällt er, unabhängig von der HR-Spalte.
    const hideTrend = explicitMonths;
    const rows = data?.rows || [];
    const subtotalsByRep = {};
    (data?.subtotals || []).forEach((s) => {
        subtotalsByRep[s.rep_code] = s;
    });

    const withSubtotals = [];
    rows.forEach((row, index) => {
        withSubtotals.push({kind: "row", row});
        const isLastOfRep = rows[index + 1]?.rep_code !== row.rep_code;
        if (isLastOfRep) {
            withSubtotals.push({kind: "subtotal", rep_code: row.rep_code, rep_name: row.rep_name});
        }
    });

    return (
        <AnalysisCard
            title="Vertreter × Monat"
            help="Je Ansprechpartner und Artikelart, für die gewählten Monate des laufenden Jahres."
            loading={loading}
            error={error}
            empty={!loading && rows.length === 0}
        >
            <StickyTableArea>
                <Table size="sm" hover className="mb-0 align-middle">
                    <thead className="sticky-top bg-white">
                        <tr>
                            <th>
                                <HeaderWithHelp help="Kundenverantwortlicher in weclapp, über den Benutzer dem Kürzel zugeordnet (Stammdaten).">
                                    Ansprechpartner
                                </HeaderWithHelp>
                            </th>
                            <th>Type / Artikelart</th>
                            <YearHeaderCells pastYears={pastYears} currentLabel={labels.current} hrLabel={labels.hr} hideHr={hideHr} />
                            <th className="text-end va-num-th">
                                <HeaderWithHelp help="Vorjahr gegenüber dem Jahr davor.">{labels.prevVsPrevprev}</HeaderWithHelp>
                            </th>
                            {explicitMonths ? (
                                <th className="text-end va-num-th" style={{minWidth: "9rem"}}>
                                    <HeaderWithHelp help="Umsatz der gewählten Monate gegenüber denselben Monaten des Vorjahres.">
                                        ∑ vs. Vorjahr (gleiche Monate)
                                    </HeaderWithHelp>
                                </th>
                            ) : (
                                <th className="text-end va-num-th">
                                    <HeaderWithHelp help="Hochrechnung (bzw. Bezugsjahr) gegenüber dem Vorjahr.">{labels.hrVsPrev}</HeaderWithHelp>
                                </th>
                            )}
                            {!hideTrend && (
                                <th className="text-end va-num-th">
                                    <HeaderWithHelp help="Hochrechnung (bzw. Bezugsjahr) gegen den Durchschnitt der vier Jahre davor." placement="left">
                                        {labels.trend}
                                    </HeaderWithHelp>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {withSubtotals.map((entry, index) => {
                            if (entry.kind === "subtotal") {
                                const subtotal = subtotalsByRep[entry.rep_code];
                                return (
                                    <tr key={`subtotal-${entry.rep_code}-${index}`} className="table-light fw-semibold">
                                        <td colSpan={2}>{entry.rep_name} Ergebnis</td>
                                        <YearValueCells years={subtotal?.years} current={subtotal?.current} hr={subtotal?.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                        <td className="text-end text-nowrap"><SignedPercentValue value={subtotal?.prev_vs_prevprev} /></td>
                                        <td className="text-end text-nowrap"><SignedPercentValue value={explicitMonths ? subtotal?.current_vs_prev : subtotal?.hr_vs_prev} /></td>
                                        {!hideTrend && <td className="text-end text-nowrap"><SignedPercentValue value={subtotal?.trend} /></td>}
                                    </tr>
                                );
                            }
                            const {row} = entry;
                            return (
                                <tr key={`${row.rep_code}-${row.article_type}`}>
                                    <td>{row.rep_name}</td>
                                    <td>{row.article_type}</td>
                                    <YearValueCells years={row.years} current={row.current} hr={row.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                    <td className="text-end text-nowrap"><SignedPercentValue value={row.prev_vs_prevprev} /></td>
                                    <td className="text-end text-nowrap"><SignedPercentValue value={explicitMonths ? row.current_vs_prev : row.hr_vs_prev} /></td>
                                    {!hideTrend && <td className="text-end text-nowrap"><SignedPercentValue value={row.trend} /></td>}
                                </tr>
                            );
                        })}
                        {data?.total && (
                            <tr className="table-secondary fw-bold">
                                <td colSpan={2}>Gesamtergebnis</td>
                                <YearValueCells years={data.total.years} current={data.total.current} hr={data.total.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                <td className="text-end text-nowrap"><SignedPercentValue value={data.total.prev_vs_prevprev} /></td>
                                <td className="text-end text-nowrap"><SignedPercentValue value={explicitMonths ? data.total.current_vs_prev : data.total.hr_vs_prev} /></td>
                                {!hideTrend && <td className="text-end text-nowrap"><SignedPercentValue value={data.total.trend} /></td>}
                            </tr>
                        )}
                    </tbody>
                </Table>
            </StickyTableArea>
        </AnalysisCard>
    );
}
